/* eslint-disable @typescript-eslint/ban-types */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Button from '@ui-elements/Button';
import { AppBar, Typography } from '@mui/material';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { rgba } from 'polished';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LogoWhiteImage from '~/assets/images/logo-gmoask-w.png';
import LogoImage from '~/assets/images/logo-gmoask.png';
import IconBeginnerGuideSrc from '~/assets/images/icon-video-beginner.png';
import { PALETTE, HEADER, FONT_SIZES, FONT_WEIGHTS, Z_INDEX } from '~/assets/styles/base';
import { ROUTES } from '~/constants/routes';
import { USER_INQUIRY } from '~/common/constants';

type WrapperProps = {
  $height?: string;
  $bgColor?: string;
  $align?: string;
};

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  align-items: center;
  padding: 0 20px;
  min-width: 700px; // ブラウザ縮小時のボタン表示崩れを防ぐ
  width: 100%;
  height: ${({ $height }) => $height};
  background-color: ${({ $bgColor }) => $bgColor};
  justify-content: ${({ $align }) => $align};
  z-index: ${Z_INDEX.header};
  margin-top: var(--gmo-header-height, 28px);
`;

const FlexWrapper = styled.div`
  display: flex;
`;

const Buttons = styled.div`
  display: flex;
  visibility: visible;
  align-items: center;
  width: 100%;
`;

const LinkButtons = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  word-break: keep-all;
`;

const Img = styled.img`
  display: block;
  margin-right: 25px;
  cursor: pointer;
  height: 19.4px;
`;

const IconBeginnerGuide = styled.img`
  display: block;
  margin-right: 12px;
  cursor: pointer;
  height: 24px;
`;

const EnqListButton = styled(Button)`
  color: ${PALETTE.text.main};
  background-color: ${PALETTE.white};
  margin-right: 23px;
  padding: 0;
  width: 144px;
  font-weight: ${FONT_WEIGHTS.base};
  word-break: keep-all;
  // スタイル上書きの優先度上げる
  && {
    min-height: 32px;
  }
  &:hover {
    background-color: ${PALETTE.gray[200]};
  }
`;

const EnqRegisterButton = styled(Button)`
  color: ${PALETTE.text.main};
  background-color: ${PALETTE.yellow.main};
  padding: 0;
  width: 180px;
  font-weight: ${FONT_WEIGHTS.base};
  && {
    min-height: 32px;
  }
  &:hover {
    background-color: ${rgba(PALETTE.yellow.main, 0.9)};
  }
`;

const LogoutButton = styled(Button)`
  word-break: keep-all;
  margin-left: auto;
  color: ${HEADER.navigationBar.logoutButtonColor.user};
  border: 1px solid ${HEADER.navigationBar.logoutButtonColor.user};
  padding: 0;
  margin-left: 8px;
  min-width: 91px;
  font-size: ${FONT_SIZES.title5};
  font-weight: ${FONT_WEIGHTS.base};
  line-height: 1;
  && {
    min-height: 32px;
  }
`;

const AdminLogoutButton = styled(Button)`
  word-break: keep-all;
  margin-left: auto;
  color: ${HEADER.navigationBar.logoutButtonColor.admin};
  border: 1px solid ${HEADER.navigationBar.logoutButtonColor.admin};
  background-color: ${PALETTE.white};
  padding: 0;
  width: 120px;
  font-size: ${FONT_SIZES.title5};
  font-weight: ${FONT_WEIGHTS.base};
  line-height: 1;
  margin-left: 8px;
  && {
    min-height: 32px;
  }
`;

const AdminBox = styled.div`
  font-size: ${FONT_SIZES.modalInputDefault};
  font-weight: ${FONT_WEIGHTS.size3};
`;

const SubHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0 20px;
  min-width: 700px;
  width: 100%;
  height: 70px;
  background-color: #e2f1fe;
  z-index: ${Z_INDEX.header};
  position: relative;
`;

const InquiryWrapper = styled.div`
  margin: 0 auto;
  max-width: 1100px;
  display: flex;
`;

const InquiryInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-right: 50px;
`;

const InquiryButtonWrapper = styled.div`
  align-items: center;
  flex-wrap: wrap;
`;

const InquiryButton = styled(Button)`
  color: ${PALETTE.text.main};
  border: 1px solid ${PALETTE.black};
  background-color: ${PALETTE.white};
  padding: 0;
  width: 144px;
  font-weight: ${FONT_WEIGHTS.base};
  word-break: keep-all;
  // スタイル上書きの優先度上げる
  && {
    min-height: 32px;
  }
  &:hover {
    background-color: ${PALETTE.gray[200]};
  }
`;

const StyledTypography = styled(Typography)`
  font-size: ${FONT_SIZES.note1};
  margin-left: 5px;
  margin-top: -2px;
  text-decoration: none;
`;

type Props = {
  height?: string;
  bgColor?: string;
  isHiddenNavButtons?: boolean;
  isHiddenLogoutButton?: boolean;
  handleLogout?: Function;
  isAdmin?: Function | boolean;
};

const NavigationBar: React.FC<Props> = ({
  bgColor = PALETTE.blue.dark,
  height,
  isHiddenNavButtons = false,
  isHiddenLogoutButton = false,
  handleLogout,
  isAdmin = false,
}): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const handleInquiry = () => {
    const inquiryUrl = `${ROUTES.USER.INQUIRY}?${USER_INQUIRY.REQUEST_PARAM}=${window.location.pathname}`;
    navigate(inquiryUrl);
  };

  return (
    <AppBar sx={{ bgcolor: bgColor, boxShadow: 'none' }}>
      <Wrapper
        $height={height}
        $bgColor={bgColor}
        $align={isAdmin ? 'space-between' : 'unset'}
        data-testid="uiParts-NavigationBar"
      >
        <FlexWrapper>
          <Img
            src={isAdmin ? LogoImage : LogoWhiteImage}
            alt="GMO Ask"
            title="GMO Ask"
            onClick={() => navigate(isAdmin ? ROUTES.ADMIN.MYPAGE : ROUTES.USER.MYPAGE)}
            data-testid="uiParts-NavigationBar-logo"
          />
          {isAdmin && (
            <AdminBox data-testid="uiParts-NavigationBar-AdminBox">
              {t('adminSignin.adminView')}
            </AdminBox>
          )}
        </FlexWrapper>
        {!isHiddenNavButtons && (
          <Buttons data-testid="NavButtons">
            <EnqListButton
              variant="text"
              onClick={() => {
                // MYPAGEからの場合は画面リフレッシュで再読み込みさせる
                if (location.pathname === ROUTES.USER.MYPAGE) {
                  navigate(0);
                } else {
                  navigate(ROUTES.USER.MYPAGE);
                }
              }}
            >
              {t('appCommon.enqueteList')}
            </EnqListButton>
            <EnqRegisterButton
              variant="text"
              onClick={() => navigate(ROUTES.USER.SURVEY_REGISTER_TYPE_SELECTION)}
            >
              {t('appCommon.enqueteRegister')}
            </EnqRegisterButton>
            <LinkButtons>
              <div style={{ display: 'inline-flex', marginRight: 5 }}>
                <Button
                  variant="text"
                  href={ROUTES.COMMON.BEGINNER_GUIDE_VIDEO}
                  width="auto"
                  sx={{ color: PALETTE.white, fontWeight: FONT_WEIGHTS.base }}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <IconBeginnerGuide
                    src={IconBeginnerGuideSrc}
                    alt="IconBeginnerGuideVideo"
                    title="IconBeginnerGuideVideo"
                    data-testid="IconBeginnerGuideVideo-icon"
                  />
                  {t('appCommon.beginnerGuide')}
                </Button>
              </div>
              <div style={{ display: 'inline-flex', marginRight: -15 }}>
                <Button
                  variant="text"
                  href={ROUTES.COMMON.INFO}
                  width="auto"
                  sx={{ color: PALETTE.white, fontWeight: FONT_WEIGHTS.base }}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <NotificationsNoneIcon sx={{ color: PALETTE.white, fontSize: 29 }} />
                </Button>
              </div>
              <div style={{ display: 'inline-flex', marginRight: 15 }}>
                <Button
                  variant="text"
                  href={ROUTES.COMMON.HELP}
                  width="auto"
                  sx={{ color: PALETTE.white, fontWeight: FONT_WEIGHTS.base }}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <HelpOutlineIcon sx={{ color: PALETTE.white, fontSize: 29 }} />
                </Button>
              </div>
            </LinkButtons>
          </Buttons>
        )}
        {!isHiddenLogoutButton && isAdmin && handleLogout && (
          <AdminLogoutButton variant="text" onClick={() => handleLogout()} dataTestId="logout">
            {t('appCommon.logout')}
          </AdminLogoutButton>
        )}
        {!isHiddenLogoutButton && !isAdmin && handleLogout && (
          <LogoutButton variant="text" onClick={() => handleLogout()} dataTestId="logout">
            {t('appCommon.logout')}
          </LogoutButton>
        )}
      </Wrapper>
      {!isAdmin && window.location.pathname !== ROUTES.USER.INQUIRY && (
        <SubHeaderWrapper data-testid="uiParts-subHeader-Inquiry">
          <InquiryWrapper>
            <InquiryInfoWrapper>
              <QuestionMarkIcon
                sx={{
                  width: 16,
                  height: 16,
                  background: 'transparent',
                  color: PALETTE.black,
                  borderRadius: '50%',
                  border: `1px solid ${PALETTE.black}`,
                }}
                data-testid="uiParts-HelpLink-Icon"
              />
              <StyledTypography data-testid="uiParts-Inquiry-Text">
                {t('appCommon.subHeader.inquiryIntro')}
              </StyledTypography>
            </InquiryInfoWrapper>
            <InquiryButtonWrapper>
              <InquiryButton
                data-testid="uiParts-Inquiry-Button"
                variant="text"
                onClick={handleInquiry}
              >
                {t('appCommon.subHeader.inquiry')}
              </InquiryButton>
            </InquiryButtonWrapper>
          </InquiryWrapper>
        </SubHeaderWrapper>
      )}
    </AppBar>
  );
};

export default NavigationBar;
